/**
 * Zakladka "Koszty z KSeF" (kontrakt GET /external-costs/z-ksef): faktury kosztowe pobrane z KSeF
 * (sugestia 'koszt' + korekty kosztowe). Domyslnie te, ktore nie sa jeszcze ani faktura zakupu, ani kosztem
 * (status=do_zaksiegowania); przelacznik statusu pokazuje tez zaksiegowane (z polem `koszt`) i pominiete.
 * Kolumny: Data · Numer (+KSeF) · Sprzedawca + NIP · Rodzaj · Netto · Brutto · Płatność (forma + termin / zapłacono /
 * po terminie z kolorem) · Rachunek (skrot, pelny w title) · Sugestia kategorii · Uwagi · Akcje (Szczegóły, Zaksięguj).
 * Filtry: szukaj, NIP, rodzaj (wszystkie / faktury / korekty), status (z licznikami), forma platnosci, zaplata,
 * zakres dat wystawienia, sugerowana kategoria. Wspolny dla ExternalCostsPage i AccountingPage.
 * `sklepy` i `kategorie` moga przyjsc z rodzica; brak = pobiera sam (locations, external-costs/categories).
 * "Szczegóły" otwiera SzczegolyKsefModal (ksef-otrzymane) w trybie 'koszty' - dane z /external-costs/z-ksef/<id>
 * (uprawnienie ksiegowosc.rozliczenia, bez admina).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { externalCostsService } from '../../services/externalCostsService';
import ZaksiegujModal from './ZaksiegujModal';
import SzczegolyKsefModal from '../ksef-otrzymane/SzczegolyKsefModal';
import {
  formatujKwote, formatujDate, komunikatBledu, SUGESTIE_KSEF, ZRODLA_SUGESTII, skrocNumerKsef, STATUSY_KOSZTU,
  STANY_LISTY_KSEF, OPCJE_FORMY, OPCJE_ZAPLATY, opisPlatnosci, skrocRachunek, formatujRachunek,
} from './format';

const NA_STRONE = 50;
const th = (tekst, extra = {}) => (
  <th key={tekst} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', ...extra }}>{tekst}</th>
);
const td = { padding: '5px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: '11px' };
const pole = { padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };
const przelacznik = (aktywny) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer',
  border: `1px solid ${aktywny ? 'var(--primary)' : 'var(--border)'}`,
  background: aktywny ? 'var(--primary)' : 'var(--card-bg)', color: aktywny ? '#fff' : 'var(--text-secondary)',
});
const btnMaly = (kolor) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer',
  border: `1px solid ${kolor}`, background: 'var(--card-bg)', color: kolor,
});

const Pigulka = ({ testId, etykieta, kolor, tlo, title }) => (
  <span data-testid={testId} title={title || undefined}
    style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: kolor, backgroundColor: tlo }}>
    {etykieta}
  </span>
);

const RODZAJE_FILTR = [
  { id: '', etykieta: 'Wszystkie' },
  { id: 'vat', etykieta: 'Faktury' },
  { id: 'korekty', etykieta: 'Korekty' },
];

const ETYKIETA_RAZEM = {
  do_zaksiegowania: (n) => `${n} do zaksięgowania`,
  zaksiegowane: (n) => `${n} zaksięgowanych`,
  pominiete: (n) => `${n} pominiętych`,
  wszystkie: (n) => `${n} faktur`,
};

/** Kolumna "Płatność": forma (1. linia) + stan z kolorem (2. linia). */
export const KomorkaPlatnosci = ({ wiersz, dzis }) => {
  const o = opisPlatnosci(wiersz, dzis);
  return (
    <div data-testid="platnosc" data-stan={o.stan} title={o.title}>
      <div data-testid="platnosc-forma" style={{ fontWeight: 600, color: o.forma ? 'var(--text)' : 'var(--text-muted)' }}>{o.forma || '—'}</div>
      <div data-testid="platnosc-stan" style={{ fontSize: '10px', fontWeight: 600, color: o.kolor }}>{o.etykieta}</div>
    </div>
  );
};

/** Kolumna "Rachunek": skrot + bank; pelny numer w title. */
export const KomorkaRachunku = ({ wiersz }) => {
  if (!wiersz?.rachunek_nr) return <span data-testid="rachunek" style={{ color: 'var(--text-muted)' }}>—</span>;
  const pelny = wiersz.rachunek_formatowany || formatujRachunek(wiersz.rachunek_nr);
  return (
    <div data-testid="rachunek" title={`${pelny}${wiersz.rachunek_bank ? ` (${wiersz.rachunek_bank})` : ''}`}>
      <div style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{skrocRachunek(wiersz.rachunek_nr)}</div>
      {wiersz.rachunek_bank && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{wiersz.rachunek_bank}</div>}
    </div>
  );
};

const zrodloSzczegolow = { szczegoly: externalCostsService.getZKsefSzczegoly, pobierzXml: externalCostsService.pobierzXmlZKsef };

export default function ZKsefTab({ sklepy: sklepyZRodzica, kategorie: kategorieZRodzica, locationId = '', onZaksiegowano, onLiczba }) {
  const [sklepy, setSklepy] = useState(sklepyZRodzica || []);
  const [kategorie, setKategorie] = useState(kategorieZRodzica || []);
  const [lista, setLista] = useState({ wiersze: [], razem: 0, strona: 1, stron: 1, liczniki: null, dzis: null });
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(null);
  const [szukaj, setSzukaj] = useState('');
  const [szukajOdlozony, setSzukajOdlozony] = useState('');
  const [nip, setNip] = useState('');
  const [nipOdlozony, setNipOdlozony] = useState('');
  const [rodzaj, setRodzaj] = useState('');
  const [status, setStatus] = useState('do_zaksiegowania');
  const [forma, setForma] = useState('');
  const [zaplata, setZaplata] = useState('');
  const [od, setOd] = useState('');
  const [doDaty, setDoDaty] = useState('');
  const [kategoriaId, setKategoriaId] = useState('');
  const [strona, setStrona] = useState(1);
  const [wybrany, setWybrany] = useState(null);
  const [podglad, setPodglad] = useState(null);
  const req = useRef(0);

  useEffect(() => { if (sklepyZRodzica) setSklepy(sklepyZRodzica); }, [sklepyZRodzica]);
  useEffect(() => { if (kategorieZRodzica) setKategorie(kategorieZRodzica); }, [kategorieZRodzica]);
  useEffect(() => { const t = setTimeout(() => setSzukajOdlozony(szukaj.trim()), 300); return () => clearTimeout(t); }, [szukaj]);
  useEffect(() => { const t = setTimeout(() => setNipOdlozony(nip.trim()), 300); return () => clearTimeout(t); }, [nip]);

  useEffect(() => {
    let aktywny = true;
    if (!sklepyZRodzica) {
      api.get('/locations').then(r => {
        if (!aktywny) return;
        const d = r?.data?.data ?? r?.data;
        setSklepy((Array.isArray(d) ? d : []).map(l => ({ id: l.id, nazwa: l.nazwa || l.name || `Sklep ${l.id}` })));
      }).catch(() => {});
    }
    if (!kategorieZRodzica) {
      externalCostsService.getCategories().then(r => { if (aktywny && r?.success) setKategorie(r.data || []); }).catch(() => {});
    }
    return () => { aktywny = false; };
  }, [sklepyZRodzica, kategorieZRodzica]);

  const laduj = useCallback(async () => {
    const nr = ++req.current;
    setLadowanie(true);
    try {
      const odp = await externalCostsService.getZKsef({
        strona, na_strone: NA_STRONE, szukaj: szukajOdlozony, nip: nipOdlozony, rodzaj, location_id: locationId,
        status, forma, zaplata, od, do: doDaty, category_id: kategoriaId,
      });
      if (nr !== req.current) return;
      const d = odp?.data || {};
      const razem = d.razem ?? 0;
      setLista({ wiersze: Array.isArray(d.wiersze) ? d.wiersze : [], razem, strona: d.strona ?? strona, stron: d.stron ?? 1,
        liczniki: d.liczniki || null, dzis: d.dzis || null });
      setBlad(null);
      // odznaka na zakladce = zawsze "do zaksiegowania" (z licznikow), niezaleznie od biezacego filtra
      if (onLiczba) onLiczba(d.liczniki?.statusy?.do_zaksiegowania ?? (status === 'do_zaksiegowania' ? razem : 0));
    } catch (err) {
      if (nr !== req.current) return;
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać listy z KSeF.'));
    } finally {
      if (nr === req.current) setLadowanie(false);
    }
  }, [strona, szukajOdlozony, nipOdlozony, rodzaj, locationId, status, forma, zaplata, od, doDaty, kategoriaId, onLiczba]);

  useEffect(() => { laduj(); }, [laduj]);
  useEffect(() => { setStrona(1); }, [szukajOdlozony, nipOdlozony, rodzaj, status, forma, zaplata, od, doDaty, kategoriaId]);

  const zaksiegowano = (koszt, wiersz) => {
    setLista(l => ({ ...l, wiersze: l.wiersze.filter(w => w.id !== wiersz.id), razem: Math.max(0, l.razem - 1) }));
    const doZaks = lista.liczniki?.statusy?.do_zaksiegowania ?? lista.razem;
    if (onLiczba) onLiczba(Math.max(0, doZaks - 1));
    if (onZaksiegowano) onZaksiegowano(koszt, wiersz);
  };

  const liczniki = lista.liczniki || {};
  const licznikStatusu = (id) => (id === 'wszystkie'
    ? Object.values(liczniki.statusy || {}).reduce((a, b) => a + (b || 0), 0)
    : liczniki.statusy?.[id]);
  const zLicznikiem = (etykieta, n) => (n === undefined || n === null ? etykieta : `${etykieta} (${n})`);
  const dzis = lista.dzis || undefined;
  const wyczyscFiltry = () => {
    setSzukaj(''); setNip(''); setRodzaj(''); setStatus('do_zaksiegowania'); setForma(''); setZaplata(''); setOd(''); setDoDaty(''); setKategoriaId('');
  };
  const saFiltry = szukaj || nip || rodzaj || status !== 'do_zaksiegowania' || forma || zaplata || od || doDaty || kategoriaId;

  return (
    <div>
      {/* Filtry - wiersz 1 */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
        <input data-testid="zksef-szukaj" type="text" placeholder="Szukaj: numer, sprzedawca, KSeF" value={szukaj} onChange={e => setSzukaj(e.target.value)} style={{ ...pole, minWidth: '220px' }} />
        <input data-testid="zksef-nip" type="text" placeholder="NIP sprzedawcy" value={nip} onChange={e => setNip(e.target.value)} style={{ ...pole, width: '140px' }} />
        <div style={{ display: 'flex', gap: '4px' }}>
          {RODZAJE_FILTR.map(r => (
            <button key={r.id || 'wszystkie'} type="button" data-testid={`zksef-rodzaj-${r.id || 'wszystkie'}`} onClick={() => setRodzaj(r.id)} style={przelacznik(rodzaj === r.id)}>{r.etykieta}</button>
          ))}
        </div>
        <button type="button" onClick={laduj} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--info)', color: 'var(--info)', background: 'var(--card-bg)', borderRadius: '4px', cursor: 'pointer' }}>Odśwież</button>
        {saFiltry && <button type="button" data-testid="zksef-wyczysc" onClick={wyczyscFiltry} style={{ ...btnMaly('var(--text-secondary)'), padding: '6px 12px', fontSize: '12px' }}>Wyczyść filtry</button>}
        <span data-testid="zksef-razem" style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-muted)' }}>
          {ladowanie ? 'Ładowanie…' : (ETYKIETA_RAZEM[status] || ETYKIETA_RAZEM.wszystkie)(lista.razem)}
        </span>
      </div>
      {/* Filtry - wiersz 2: status / forma / zaplata / daty / kategoria */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        <div data-testid="zksef-statusy" style={{ display: 'flex', gap: '4px' }}>
          {STANY_LISTY_KSEF.map(s => (
            <button key={s.id} type="button" data-testid={`zksef-status-${s.id}`} onClick={() => setStatus(s.id)} style={przelacznik(status === s.id)}
              title={s.id === 'zaksiegowane' ? 'Faktury już zaksięgowane jako koszt (z numerem kosztu)' : s.id === 'pominiete' ? 'Faktury pominięte w zakładce KSeF' : undefined}>
              {zLicznikiem(s.etykieta, licznikStatusu(s.id))}
            </button>
          ))}
        </div>
        <select data-testid="zksef-forma" value={forma} onChange={e => setForma(e.target.value)} style={pole} title="Forma płatności z XML faktury (Fa/Platnosc/FormaPlatnosci)">
          {OPCJE_FORMY.map(o => <option key={o.id || 'wszystkie'} value={o.id}>{zLicznikiem(o.etykieta, o.klucz ? liczniki.formy?.[o.klucz] : undefined)}</option>)}
        </select>
        <select data-testid="zksef-zaplata" value={zaplata} onChange={e => setZaplata(e.target.value)} style={pole} title="Wg XML: Zaplacono=1 albo termin płatności względem dzisiejszej daty">
          {OPCJE_ZAPLATY.map(o => <option key={o.id || 'wszystkie'} value={o.id}>{zLicznikiem(o.etykieta, o.id ? liczniki.zaplata?.[o.id] : undefined)}</option>)}
        </select>
        <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
          wystawiona od <input data-testid="zksef-od" type="date" value={od} onChange={e => setOd(e.target.value)} style={pole} />
        </label>
        <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
          do <input data-testid="zksef-do" type="date" value={doDaty} onChange={e => setDoDaty(e.target.value)} style={pole} />
        </label>
        <select data-testid="zksef-kategoria" value={kategoriaId} onChange={e => setKategoriaId(e.target.value)} style={pole} title="Sugerowana kategoria (z reguł po NIP sprzedawcy)">
          <option value="">Kategoria (sugerowana): wszystkie</option>
          {kategorie.map(k => <option key={k.id} value={k.id}>{k.nazwa}</option>)}
        </select>
      </div>

      {blad && <div data-testid="zksef-blad" style={{ fontSize: '11px', color: 'var(--danger)', marginBottom: '8px' }}>{blad}</div>}

      <div style={{ overflow: 'auto', background: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              {th('Data')}{th('Numer')}{th('Sprzedawca')}{th('Rodzaj')}{th('Netto', { textAlign: 'right' })}{th('Brutto', { textAlign: 'right' })}
              {th('Płatność')}{th('Rachunek')}{th('Sugestia kategorii')}{th('Uwagi')}{th('Akcje', { textAlign: 'center' })}
            </tr>
          </thead>
          <tbody>
            {!ladowanie && lista.wiersze.length === 0 && (
              <tr><td colSpan={11} style={{ ...td, textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                {status === 'do_zaksiegowania' && !saFiltry ? 'Brak faktur kosztowych z KSeF do zaksięgowania.' : 'Brak faktur z KSeF dla wybranych filtrów.'}
              </td></tr>
            )}
            {lista.wiersze.map(w => {
              const s = SUGESTIE_KSEF[w.sugestia] || SUGESTIE_KSEF.koszt;
              const sk = w.sugestia_kategorii || {};
              const stan = w.stan || 'do_zaksiegowania';
              const st = w.koszt ? (STATUSY_KOSZTU[w.koszt.status] || null) : null;
              return (
                <tr key={w.id} data-testid="wiersz-zksef" data-stan={stan} style={{ background: w.koszt_istniejacy ? 'var(--danger-light)' : 'transparent', opacity: stan === 'pominiete' ? 0.7 : 1 }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatujDate(w.data_wystawienia)}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{w.numer_faktury || '—'}</div>
                    <div data-testid="numer-ksef" title={w.ksef_numer || ''} style={{ fontSize: '10px', color: 'var(--text-muted)' }}>KSeF {skrocNumerKsef(w.ksef_numer)}</div>
                  </td>
                  <td style={td}>
                    <div>{w.sprzedawca_nazwa || '—'}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>NIP {w.sprzedawca_nip || '—'}</div>
                  </td>
                  <td style={td}><Pigulka testId="rodzaj" etykieta={s.etykieta} kolor={s.kolor} tlo={s.tlo} title={s.opis} /></td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatujKwote(w.netto)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{formatujKwote(w.brutto)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}><KomorkaPlatnosci wiersz={w} dzis={dzis} /></td>
                  <td style={td}><KomorkaRachunku wiersz={w} /></td>
                  <td style={td}>
                    {sk.category_id
                      ? <span data-testid="sugestia-kategorii" title={ZRODLA_SUGESTII[sk.zrodlo] || ''} style={{ color: 'var(--purple)', fontWeight: 600 }}>{sk.kategoria_nazwa}</span>
                      : <span data-testid="sugestia-kategorii" style={{ color: 'var(--text-muted)' }}>brak podpowiedzi</span>}
                  </td>
                  <td style={{ ...td, fontSize: '10px', color: 'var(--text-secondary)' }}>
                    {w.koszt && (
                      <div data-testid="uwaga-koszt">
                        koszt #{w.koszt.id}{st ? <> · <span style={{ color: st.kolor, fontWeight: 600 }}>{st.etykieta}</span></> : null}
                        {w.koszt.status === 'zaplacony' && w.koszt.data_platnosci ? ` ${formatujDate(w.koszt.data_platnosci)}` : ''}
                      </div>
                    )}
                    {stan === 'pominiete' && <div data-testid="uwaga-pominieta">pominięta{w.powod_pominiecia ? `: ${w.powod_pominiecia}` : ''}</div>}
                    {w.koszt_istniejacy && !w.koszt && <div data-testid="uwaga-istniejacy" style={{ color: 'var(--danger)' }}>już zaksięgowany jako koszt #{w.koszt_istniejacy.id}</div>}
                    {w.sugestia === 'korekta' && (w.korygowana_koszt
                      ? <div data-testid="uwaga-korekta">koryguje koszt #{w.korygowana_koszt.id}</div>
                      : <div data-testid="uwaga-korekta" style={{ color: 'var(--orange)' }}>brak kosztu faktury korygowanej</div>)}
                    {!w.ma_xml && <div>bez XML — kwoty z metadanych, bez terminu</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      <button type="button" data-testid="btn-szczegoly" onClick={() => setPodglad(w)} title="Płatność, rachunki, warunki transakcji, pozycje z XML"
                        style={btnMaly('var(--info)')}>
                        Szczegóły
                      </button>
                      {stan === 'do_zaksiegowania' && (
                        <button type="button" data-testid="btn-zaksieguj" onClick={() => setWybrany(w)} disabled={!w.mozna_zaksiegowac}
                          title={w.mozna_zaksiegowac ? 'Zaksięguj jako koszt' : 'Już zaksięgowany'}
                          style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: w.mozna_zaksiegowac ? 'pointer' : 'not-allowed', border: '1px solid var(--success)', background: 'var(--success)', color: '#fff', opacity: w.mozna_zaksiegowac ? 1 : 0.5 }}>
                          Zaksięguj
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lista.stron > 1 && (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center', padding: '10px', fontSize: '11px' }}>
          <button type="button" disabled={strona <= 1} onClick={() => setStrona(s => s - 1)} style={przelacznik(false)}>‹ poprzednia</button>
          <span>strona {lista.strona} / {lista.stron}</span>
          <button type="button" disabled={strona >= lista.stron} onClick={() => setStrona(s => s + 1)} style={przelacznik(false)}>następna ›</button>
        </div>
      )}

      {wybrany && (
        <ZaksiegujModal wiersz={wybrany} kategorie={kategorie} sklepy={sklepy} locationId={locationId}
          onZamknij={() => setWybrany(null)} onZaksiegowano={zaksiegowano} />
      )}
      {podglad && (
        <SzczegolyKsefModal id={podglad.id} wiersz={podglad} sklepy={sklepy} tryb="koszty" zrodlo={zrodloSzczegolow}
          onZamknij={() => setPodglad(null)} />
      )}
    </div>
  );
}
